<template>
  <div
    id="article-card"
    class="d-flex align-items-center cursor-pointer px-4 py-3 border-bottom"
    :class="{ 'article-selected': isArticleSelected, 'with-alert': alertDisplayed }"
    @click="selectArticle()"
    data-test="article-card"
    :data-uuid="`${this.article.encodedExternalId}`"
  >
    <div
      class="d-flex align-items-center cursor-pointer h-100 w-100"
    >
      <label
        v-if="!shareMode"
        class="custom-switch-label d-flex flex-column align-self-center m-0 mr-4"
        :class="[{ 'cursor-not-allowed' : readonlyMode }]"
      >
        <input
          type="checkbox"
          name="custom-switch-checkbox"
          class="custom-switch-input"
          :checked="isArticleShared"
          @click="toggleShare($event)"
          :disabled="readonlyMode"
        >
        <span
          ref="custom-switch-indicator"
          class="custom-switch-indicator"
          :data-test="`share-toggle-${index}`"
        />
        <span>Share</span>
      </label>
      <div
        class="d-flex flex-column justify-content-between h-100 w-100"
      >
        <p
          v-if="alertDisplayed"
          class="mb-0 font-weight-bold"
        >
          <span class="icon">
            <i
              class="fas fa-exclamation-triangle warning"
            />
          </span>
          Creative missing
        </p>
        <p
          id="article-title"
          class="text-wrap"
          data-test="article-title"
        >
          {{ articleTitle }}
        </p>
        <div>
          <p
            class="small text-muted"
            v-if="!shareMode"
          >
            State: {{ articleState }}
          </p>
          <div
            class="d-flex justify-content-between"
          >
            <p
              class="small text-muted"
            >
              Edited: {{ articleEditedDate }}
            </p>
            <p
              :class="['text-muted flex-shrink-0', {'hidden': !hasComments}]"
              :key="selectedCreativeId"
              data-test="comment-count"
            >
              <i class="fal fa-comment-alt" />
              {{ articleCommentsCount }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import {
  mapActions, mapState, mapGetters,
} from 'vuex';
import * as contentTypes from '@/modules/content/store/types';
import dateHelper from '@/core/mixins/dateHelper';

export default {
  name: 'ArticleCard',
  mixins: [
    dateHelper,
  ],
  props: {
    shareMode: {
      type: Boolean,
      default: true,
    },
    readonlyMode: {
      type: Boolean,
      default: true,
    },
    article: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      default: () => 0,
    },
  },
  data() {
    return {
      isToggling: false,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
    ]),
    ...mapState('content', [
      'selectedCreativeId',
      'selectedArticleUuid',
      'commentsCounts',
    ]),
    ...mapGetters('content', [
      contentTypes.GET_CONTENT_ITEMS_WITH_MISSED_CREATIVES,
      contentTypes.GET_SUCCESSFULLY_SCRAPED_CONTENT_ITEMS,
    ]),
    isArticleShared() {
      return this.article.shared;
    },
    articleTitle() {
      return this.article.title || '';
    },
    articleState() {
      return this.article.state || '';
    },
    articleEditedDate() {
      return this.formatDateForPlatform(this.article.updatedDate) || '';
    },
    isArticleSelected() {
      if (!this.selectedArticleUuid && this.index === 0) this.selectArticle();
      return this.selectedArticleUuid === this.article.encodedExternalId;
    },
    hasComments() {
      return this.commentsCounts.hasOwnProperty(this.article.encodedExternalId);
    },
    articleCommentsCount() {
      return this.commentsCounts[this.article.encodedExternalId]?.count;
    },
    alertDisplayed() {
      return this.GET_CONTENT_ITEMS_WITH_MISSED_CREATIVES.includes(this.article.encodedExternalId);
    },
  },
  methods: {
    ...mapActions('content', [
      contentTypes.SET_SELECTED_ARTICLE_UUID,
      contentTypes.TOGGLE_ARTICLE_SHARED_STATE,
    ]),
    selectArticle() {
      this.SET_SELECTED_ARTICLE_UUID(this.article.encodedExternalId);
    },
    async toggleShare(event) {
      event.stopPropagation();
      const isChecked = event.target.checked;
      this.isToggling = true;
      const payload = {
        isChecked,
        contentItem: {
          contentItemUUID: this.article.encodedExternalId,
          campaignId: this.campaign.id,
        },
      };
      await this.TOGGLE_ARTICLE_SHARED_STATE(payload);
      this.isToggling = false;
    },
  },
};
</script>
<style scoped lang="scss">
* {
  margin: 0;
  padding: 0;
}
#article-card {
  height: 140px;
}
#article-card.with-alert {
  height: 185px;
}
.article-selected {
  background-color: $gray-100;
}
#article-img-container {
  width: 104px;
  height: 104px;
}
#article-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
#article-title {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
  line-height: 1.25rem;
}
.small {
  font-size: 0.75rem;
}
.hidden {
  visibility: hidden;
}
</style>
